import { CommonModule } from '@angular/common';
import { Component, effect } from '@angular/core';
import anime from 'animejs/lib/anime.es.js';

import { AppStateService } from 'app/services/app-state.service';

import { AgentSetupWatcherComponent } from "../agent-setup-watcher/agent-setup-watcher.component";
import { SecondaryBtnComponent } from "app/components/ui-kit/buttons/secondary-btn/secondary-btn.component";
import { RequestConnectionOverlayComponent } from 'app/components/distinct-components/overlays/request-connection-overlay/request-connection-overlay.component';
import { FlightOptionCardComponent } from 'app/components/distinct-components/cards/flight-option-card/flight-option-card.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { SectionLabelComponent } from 'app/components/ui-kit/typography/section-label/section-label.component';
import { ToggleItemRowComponent } from 'app/components/ui-kit/toggles/toggle-item-row/toggle-item-row.component';
import { ProviderNames, normalizeProvider } from 'app/constants/provider-constants';
import { holdPls } from 'app/utils/time/timing-utils';

@Component({
  selector: 'app-agent-results',
  standalone: true,
  imports: [CommonModule, FlightOptionCardComponent, SvgLoaderComponent, RequestConnectionOverlayComponent, AgentSetupWatcherComponent, SecondaryBtnComponent, SectionLabelComponent, ToggleItemRowComponent],
  templateUrl: './agent-results.component.html',
  styleUrl: './agent-results.component.scss'
})
export class AgentResultsComponent {
  results: any;
  showAssignToAgent: boolean = false;
  providerOptions: { value: string; label: string }[] = [];
  selectedProvider: string = ProviderNames.ALL_INDEPENDENT;
  exactFiltered: any[] = [];
  similarFilteredSections: any[] = [];
  totalVisibleMatches = 0;
  private preferredProviders: string[] = [];

  constructor(public appState: AppStateService) {
    effect(() => {
      if (this.appState.agent.mode()) {
        this.appState.agent.selectedOption.set(null);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    this.selectedProvider = ProviderNames.ALL_INDEPENDENT;
    const results = this.appState.agent.refinedFlightOptions() ?? {};
    const profile = this.appState.user.profile();
    if (profile && profile.preferredProviders && profile.preferredProviders.length > 0) {
      this.preferredProviders = profile.preferredProviders.map(normalizeProvider);
    }
    this.results = {
      ...results,
      optimalMatches: this.sortOptionsByPreferredProviders(results?.optimalMatches),
      similarMatches: results?.similarMatches?.map(section => ({
        ...section,
        options: this.sortOptionsByPreferredProviders(section.options)
      }))
    };
    this.configureProviderOptions();
    this.applyProviderFilter();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.animateResults();
    }, 100);
  }

  async onFilterValueChange() {
    this.applyProviderFilter();
    await holdPls(500);
    this.animateResults();
  }

  animateResults() {
    anime({
      targets: '#r-card',
      duration: 650,
      opacity: [0, 1],
      translateY: [12, 0],
      delay: anime.stagger(75),
      direction: 'alternate',
      easing: 'spring(1, 80, 10, 2)',
      loop: false,
    });
  }

  onClickResult(r) {
    const selectedResult = r;
    this.appState.agent.selectedOption.set(selectedResult);
  }

  onClickAssignToAgent() {
    this.showAssignToAgent = true;
  }

  onClickCancelAssign() {
    this.showAssignToAgent = false;
  }

  filterOptions(options: any[]) {
    if (!options) return [];
    const filteredOptions = this.selectedProvider === ProviderNames.ALL_INDEPENDENT
      ? [...options]
      : options.filter(o => {
        const providers = this.getOptionProviders(o);
        return providers.includes(this.selectedProvider);
      });

    return this.sortOptionsByPreferredProviders(filteredOptions);
  }

  clearProviderFilter() {
    this.selectedProvider = ProviderNames.ALL_INDEPENDENT;
    this.applyProviderFilter();
    this.animateResults();
  }

  get selectedProviderLabel(): string {
    return this.providerOptions.find(option => option.value === this.selectedProvider)?.label || 'All';
  }

  private applyProviderFilter() {
    this.exactFiltered = this.filterOptions(this.results?.optimalMatches || []);
    this.similarFilteredSections = (this.results?.similarMatches || [])
      .map(section => ({
        ...section,
        options: this.filterOptions(section.options || [])
      }))
      .filter(section => section.options.length > 0);
    this.totalVisibleMatches =
      this.exactFiltered.length +
      this.similarFilteredSections.reduce((total, section) => total + section.options.length, 0);
  }

  private configureProviderOptions() {
    const providers = new Set<string>();
    [
      ...(this.results?.optimalMatches || []),
      ...((this.results?.similarMatches || []).flatMap(section => section.options || [])),
    ].forEach(option => this.getOptionProviders(option).forEach(provider => providers.add(provider)));

    const options = [
      { value: ProviderNames.ALL_INDEPENDENT, label: 'All' },
      { value: ProviderNames.NETJETS, label: ProviderNames.NETJETS },
      { value: ProviderNames.WHEELS_UP, label: 'Wheels Up' },
      { value: ProviderNames.FLEXJET, label: 'Flexjet' },
    ];

    this.providerOptions = [
      options[0],
      ...options.slice(1).filter(option => providers.has(option.value))
    ];

    if (!this.providerOptions.some(option => option.value === this.selectedProvider)) {
      this.selectedProvider = ProviderNames.ALL_INDEPENDENT;
    }
  }

  private getOptionProviders(option: any): string[] {
    const membershipProviders = Array.isArray(option?.memberships)
      ? option.memberships.map(m => normalizeProvider(m.organization ?? m.provider ?? 'Independent'))
      : [];
    const provider = normalizeProvider(option?.membershipOrganization || option?.provider || '');
    return Array.from(new Set([...membershipProviders, provider].filter(Boolean)));
  }

  private getProviderPriority(option: any): number {
    if (this.preferredProviders.length === 0) {
      return Number.POSITIVE_INFINITY;
    }

    const providers = this.getOptionProviders(option);
    const priority = providers
      .map(p => this.preferredProviders.indexOf(p))
      .filter(index => index >= 0)
      .sort((a, b) => a - b)[0];

    return priority !== undefined ? priority : Number.POSITIVE_INFINITY;
  }

  private sortOptionsByPreferredProviders(options: any[] = []): any[] {
    if (!options?.length || this.preferredProviders.length === 0) {
      return options ?? [];
    }

    return [...options].sort((a, b) => this.getProviderPriority(a) - this.getProviderPriority(b));
  }
}
